import React, { useEffect, useMemo } from "react";
import { Typography, Spin } from "antd";
import { useDpatIndicatorCmsAndMov } from "hooks/useDpatIndicatorCmsAndMov";
import {
  formatDpatSlugAsCodeLabel,
  getIndicatorRawCode,
  resolveThematicAreasText,
} from "utils/dpatIndicatorCmsHelpers";

function formatCompactIndicatorCode(rawCodeOrSlug) {
  if (!rawCodeOrSlug) return "";

  const text = String(rawCodeOrSlug).trim().replace(/–|—/g, "-");

  const labeledRangeMatch = text.match(/^(CI|SDI|PI)\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/i);
  if (labeledRangeMatch) {
    return `${labeledRangeMatch[1].toUpperCase()} ${labeledRangeMatch[3]}`;
  }

  const slugRangeMatch = text.toLowerCase().match(/^(c|sdi|pi)(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (slugRangeMatch) {
    const prefix = slugRangeMatch[1] === "c" ? "CI" : slugRangeMatch[1].toUpperCase();
    return `${prefix} ${slugRangeMatch[3]}`;
  }

  return text;
}

function splitScoringCriteriaBlocks(text) {
  if (!text || typeof text !== "string") return [];
  const normalized = text.replace(/\r\n/g, "\n").trim();
  let parts = normalized
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length > 1) return parts;
  const splitByLead = normalized.split(/\n(?=\s*(?:\(?[ivxlcdm]+\)|\d+[\).]|[a-z]\)))/i);
  const trimmed = splitByLead.map((p) => p.trim()).filter(Boolean);
  if (trimmed.length > 1) return trimmed;
  return normalized ? [normalized] : [];
}

/**
 * CMS-driven title, assessment guide, scoring criteria, and DHIS2-backed Means of verification for DPAT sheets.
 * Use embedded=true when rendered inside DpatSubIndicatorSection (hides duplicate code chip / outer card).
 */
function DpatIndicatorCmsIntro({
  year,
  slug,
  dhis2ProgramId,
  fallbackTitle,
  fallbackGuide,
  movDefaultText,
  onCmsIndicator,
  embedded = true,
}) {
  const { cmsIndicator, meansOfVerificationFromAxios, cmsLoading } = useDpatIndicatorCmsAndMov(
    year,
    slug,
    dhis2ProgramId
  );

  const scoringBlocks = useMemo(
    () => splitScoringCriteriaBlocks(cmsIndicator?.scoringCriteria),
    [cmsIndicator?.scoringCriteria]
  );

  useEffect(() => {
    if (typeof onCmsIndicator === "function") onCmsIndicator(cmsIndicator);
  }, [cmsIndicator, onCmsIndicator]);

  const thematicAreasText = cmsLoading ? "" : resolveThematicAreasText(cmsIndicator);
  const rawCodeLabel = cmsLoading
    ? null
    : getIndicatorRawCode(cmsIndicator) || formatDpatSlugAsCodeLabel(slug);
  const codeLabel = rawCodeLabel ? formatCompactIndicatorCode(rawCodeLabel) : null;
  const showMetaRow = Boolean(thematicAreasText || codeLabel || cmsLoading);

  const titleText = cmsLoading ? null : cmsIndicator?.indicatorName || fallbackTitle;
  const guideBody = cmsLoading ? null : cmsIndicator?.assessmentGuide ? (
    <Typography.Paragraph className="!mb-0 whitespace-pre-wrap text-sm text-foreground">
      {cmsIndicator.assessmentGuide}
    </Typography.Paragraph>
  ) : (
    fallbackGuide
  );
  const cmsMov =
    typeof cmsIndicator?.meansOfVerification === "string" ? cmsIndicator.meansOfVerification.trim() : "";
  const movBody = cmsLoading
    ? null
    : cmsMov || meansOfVerificationFromAxios || movDefaultText;

  const panelClass = "rounded-lg border border-border bg-muted/30 p-4";

  return (
    <div className="space-y-5">
      {showMetaRow ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
          <span className="min-w-0 flex-1 text-lg font-bold text-foreground">
            {cmsLoading ? <Spin size="small" /> : thematicAreasText || null}
          </span>
          {cmsLoading ? (
            <Spin size="small" />
          ) : codeLabel ? (
            <span className="inline-flex shrink-0 items-center rounded-md border border-[#16325a] bg-background px-2.5 py-1 text-xs font-semibold tracking-wide text-[#16325a]">
              {codeLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      {!embedded && titleText ? (
        <h3 className="text-base font-semibold text-foreground">
          {cmsLoading ? <Spin size="small" /> : titleText}
        </h3>
      ) : embedded && titleText ? (
        <h3 className="text-base font-semibold leading-snug text-foreground">
          {cmsLoading ? <Spin size="small" /> : titleText}
        </h3>
      ) : cmsLoading && !showMetaRow ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Assessment guide / requirement
        </p>
        <div className={panelClass}>
          {cmsLoading ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Spin />
              <Typography.Text type="secondary">Loading indicator details…</Typography.Text>
            </div>
          ) : guideBody ? (
            guideBody
          ) : (
            <Typography.Text type="secondary" className="text-sm">
              No assessment guide configured in CMS for {formatDpatSlugAsCodeLabel(slug)}.
            </Typography.Text>
          )}
        </div>
      </div>

      {!cmsLoading && scoringBlocks.length > 0 ? (
        <div className="space-y-3">
          {/* <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Scoring criteria{scoringBlocks.length > 1 ? ` (${scoringBlocks.length} items)` : ""}
          </p> */}
          {scoringBlocks.map((block, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-border border-l-[3px] border-l-[#16325a] bg-card p-4 text-sm text-foreground shadow-sm"
            >
              <Typography.Paragraph className="!mb-0 whitespace-pre-wrap">{block}</Typography.Paragraph>
            </div>
          ))}
        </div>
      ) : !cmsLoading ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Scoring criteria
          </p>
          <div className={panelClass}>
            <Typography.Text type="secondary" className="text-sm">
              No scoring criteria configured in CMS for {formatDpatSlugAsCodeLabel(slug)}.
            </Typography.Text>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Means of verification
        </p>
        <div className={panelClass}>
          {cmsLoading ? (
            <div className="flex justify-center py-6">
              <Spin size="small" />
            </div>
          ) : movBody ? (
            <Typography.Paragraph className="!mb-0 whitespace-pre-wrap text-sm text-foreground">
              {movBody}
            </Typography.Paragraph>
          ) : (
            <Typography.Text type="secondary" className="text-sm">
              No means of verification configured in CMS for {formatDpatSlugAsCodeLabel(slug)}.
            </Typography.Text>
          )}
        </div>
      </div>
    </div>
  );
}

export default DpatIndicatorCmsIntro;
